import { z } from 'zod';
// PROJECTS-1: creating and managing projects, their buildings and floors, in Master Data
// ("ניהול פרויקטים"). Creating needs `organization.project.create` (organization-wide); editing a
// project needs `projects.project.edit`, its buildings and floors `projects.structure.edit`.
export const PROJECT_STATUSES = ['setup', 'active', 'warranty', 'closed'];
export const PROJECT_STATUS_LABELS = {
    setup: 'בהקמה',
    active: 'פעיל',
    warranty: 'בתקופת בדק',
    closed: 'סגור',
};
export const STRUCTURE_KINDS = [
    'residential',
    'parking',
    'storage',
    'commercial',
    'other',
];
export const STRUCTURE_KIND_LABELS = {
    residential: 'בניין מגורים',
    parking: 'חניון / מרתף',
    storage: 'מחסנים',
    commercial: 'מסחר',
    other: 'אחר',
};
export const PROJECT_CODE_PATTERN = /^[A-Z0-9-]{2,12}$/;
export const PROJECT_LIMITS = {
    name: 80,
    city: 80,
    address: 200,
    description: 2000,
    structureCode: 12,
    structureName: 80,
    levelLabel: 40,
    minLevel: -20,
    maxLevel: 200,
    /** Floors added at once. */
    levelRange: 60,
};
/** The default label of a floor: ground floor, "קומה N", or the basement's number ("-1"). */
export const defaultLevelLabel = (n) => n === 0 ? 'קרקע' : n > 0 ? `קומה ${String(n)}` : String(n);
const text = (max, min = 2) => z.string().trim().min(min).max(max);
const optional = (max) => z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((value) => (value === '' ? null : value));
/** How far the project's setup is (counts of what it has). */
export const projectSetupSchema = z
    .object({
    buildings: z.number().int(),
    floors: z.number().int(),
    apartments: z.number().int(),
    members: z.number().int(),
    membersWithRoles: z.number().int(),
    areas: z.number().int(),
    workCategories: z.number().int(),
    contractors: z.number().int(),
    templateItems: z.number().int(),
    /** Active general handover checks (keys, remotes…). */
    generalChecks: z.number().int(),
    /** The organization has an acknowledgment wording: without it no handover can be closed. */
    acknowledgment: z.boolean(),
    /** The company master checklist version the project follows, and the latest one (DR-195). */
    masterVersion: z.number().int().nullable(),
    masterLatest: z.number().int().nullable(),
})
    .strict();
export const projectAdminItemSchema = z
    .object({
    id: z.uuid(),
    code: z.string(),
    name: z.string(),
    city: z.string(),
    address: z.string().nullable(),
    description: z.string().nullable(),
    status: z.enum(PROJECT_STATUSES),
    rowVersion: z.number().int().min(1),
    setup: projectSetupSchema,
    /** Work that blocks closing the project. */
    openWork: z.object({ appointments: z.number().int(), drafts: z.number().int() }).strict(),
    permissions: z.object({ edit: z.boolean(), structures: z.boolean() }).strict(),
})
    .strict();
/** `GET /project-admin`: the projects the caller may manage. */
export const projectAdminListResponseSchema = z
    .object({
    items: z.array(projectAdminItemSchema),
    permissions: z.object({ create: z.boolean() }).strict(),
})
    .strict();
export const projectCreateSchema = z
    .object({
    name: text(PROJECT_LIMITS.name),
    /** Optional: generated (P-0001, …) when empty. */
    code: z
        .string()
        .trim()
        .toUpperCase()
        .regex(PROJECT_CODE_PATTERN)
        .or(z.literal(''))
        .nullable()
        .transform((value) => (value === '' ? null : value)),
    city: text(PROJECT_LIMITS.city),
    address: optional(PROJECT_LIMITS.address),
    description: optional(PROJECT_LIMITS.description),
})
    .strict();
export const projectUpdateSchema = z
    .object({
    rowVersion: z.number().int().min(1),
    name: text(PROJECT_LIMITS.name),
    code: z.string().trim().toUpperCase().regex(PROJECT_CODE_PATTERN),
    city: text(PROJECT_LIMITS.city),
    address: optional(PROJECT_LIMITS.address),
    description: optional(PROJECT_LIMITS.description),
    status: z.enum(PROJECT_STATUSES),
})
    .strict();
export const adminLevelSchema = z
    .object({
    id: z.uuid(),
    number: z.number().int(),
    label: z.string(),
    rowVersion: z.number().int().min(1),
    /** Units on the floor: its number cannot change while there are any. */
    units: z.number().int(),
})
    .strict();
export const adminStructureSchema = z
    .object({
    id: z.uuid(),
    code: z.string(),
    name: z.string(),
    kind: z.enum(STRUCTURE_KINDS),
    status: z.enum(['active', 'archived']),
    rowVersion: z.number().int().min(1),
    units: z.number().int(),
    levels: z.array(adminLevelSchema),
})
    .strict();
/** `GET /project-admin/:id/structures`. */
export const adminStructuresResponseSchema = z
    .object({
    items: z.array(adminStructureSchema),
    permissions: z.object({ edit: z.boolean() }).strict(),
})
    .strict();
export const structureInputSchema = z
    .object({
    code: z.string().trim().min(1).max(PROJECT_LIMITS.structureCode),
    name: text(PROJECT_LIMITS.structureName, 1),
    kind: z.enum(STRUCTURE_KINDS),
})
    .strict();
export const structureUpdateSchema = structureInputSchema
    .extend({ rowVersion: z.number().int().min(1), status: z.enum(['active', 'archived']) })
    .strict();
const levelNumber = z.number().int().min(PROJECT_LIMITS.minLevel).max(PROJECT_LIMITS.maxLevel);
/** Adds the missing floors from `from` to `to` (inclusive), with default labels. */
export const levelRangeSchema = z
    .object({ from: levelNumber, to: levelNumber })
    .strict()
    .refine((r) => r.to >= r.from && r.to - r.from < PROJECT_LIMITS.levelRange, {
    message: 'invalid',
    path: ['to'],
});
export const levelUpdateSchema = z
    .object({
    rowVersion: z.number().int().min(1),
    number: levelNumber,
    label: text(PROJECT_LIMITS.levelLabel, 1),
})
    .strict();
/** `POST /projects/:id/inspection-template/copy`: copy another project's template (if empty). */
export const templateCopyRequestSchema = z.object({ sourceProjectId: z.uuid() }).strict();
/** Hebrew explanations of refusals (422 field codes and 409 conflict codes). */
export const PROJECT_ADMIN_MESSAGES = {
    duplicate: 'הקוד כבר קיים. בחרו קוד אחר, או השאירו ריק ליצירה אוטומטית.',
    code_in_use: 'לא ניתן לשנות את קוד הפרויקט אחרי שנוספו לו יחידות.',
    structure_in_use: 'לבניין יש יחידות: לא ניתן לשנות את הקוד שלו או להעבירו לארכיון.',
    level_in_use: 'בקומה יש יחידות: לא ניתן לשנות את מספרה (אפשר לשנות את שמה).',
    open_work: 'לא ניתן לסגור את הפרויקט כל עוד יש בו מועדי מסירה פעילים או טיוטות מסירה פתוחות.',
    setup_incomplete: 'לא ניתן להפעיל פרויקט בלי בניין אחד לפחות עם קומות.',
    stale: 'הנתונים עודכנו במקום אחר. טענו מחדש ונסו שוב.',
    not_empty: 'לפרויקט כבר יש תבנית בדיקה. ניתן לערוך אותה במקום להעתיק.',
    source_empty: 'בפרויקט המקור אין תבנית בדיקה.',
};
//# sourceMappingURL=project-admin.js.map